// The admin console's tenant tabs (.design-specs/admin-console.plan.md §5): every band (fixtures included), every
// band's audience on every platform, and every person. Read-only (R1). Each list is filterable by a search box and
// can hide the fixture accounts, which on dev and qa otherwise crowd out the real ones.
import { useMemo, useState } from "react";
import { Card, DataTable, EmptyState, Field, PlatformIcon, StatDelta, Switch, Tag, type DataTableColumn } from "@cntrl/ui";
import { errorMessage } from "../../app/hooks/apiError.js";
import {
  useAdminBands,
  useAdminPeople,
  useAdminSocials,
  type AdminBandRow,
  type AdminPersonRow,
  type AdminSocialRow,
} from "../../app/hooks/useAdmin.js";
import { ago, dash, day, exact, figure, matches, platformLabel } from "./adminFormat.js";
import styles from "./AdminDashboard.module.scss";

/** The search box + fixture switch every tenant list shares. */
function ListControls({
  query, onQuery, hideFixtures, onHideFixtures, placeholder, count,
}: {
  query: string; onQuery: (q: string) => void; hideFixtures: boolean; onHideFixtures: (v: boolean) => void;
  placeholder: string; count: string;
}) {
  return (
    <div className={styles.controls}>
      <div className={styles.search}>
        <Field label="Search" hideLabel type="search" placeholder={placeholder} value={query}
          onChange={(e) => onQuery(e.target.value)} />
      </div>
      {/* The switch names itself ("Hide fixture accounts"); the visible words beside it are for sighted readers. */}
      <span className={styles.toggle}>
        <Switch checked={hideFixtures} onChange={onHideFixtures} label="Hide fixture accounts" size="sm" />
        <span aria-hidden="true">Hide fixtures</span>
      </span>
      <span className={styles.count}>{count}</span>
    </div>
  );
}

function Fixture() {
  return (
    <Tag tone="neutral" size="sm">
      fixture
    </Tag>
  );
}

function Loading({ title }: { title: string }) {
  return <Card title={title} subtitle="Reading…" />;
}

function Failed({ title, error }: { title: string; error: unknown }) {
  return (
    <Card title={title}>
      <p className={styles.error}>{errorMessage(error, "Could not read this list.")}</p>
    </Card>
  );
}

// ---- Bands -------------------------------------------------------------------------------------------------

function Plan({ b }: { b: AdminBandRow }) {
  if (!b.planStatus) return <span className={styles.zero}>No plan</span>;
  const active = ["active", "lifetime", "past_due"].includes(b.planStatus);
  return (
    <span className={styles.inline}>
      <Tag tone={active ? "success" : "neutral"} size="sm">
        {b.planStatus}
      </Tag>
      {b.tier === "pro" && (
        <Tag tone="accent" size="sm">
          Pro
        </Tag>
      )}
      {b.planSource && b.planSource !== "stripe" && <span className={styles.zero}>{b.planSource}</span>}
    </span>
  );
}

function Connections({ b }: { b: AdminBandRow }) {
  const providers = b.connectedProviders ?? [];
  if (providers.length === 0) return <span className={styles.zero}>None</span>;
  const errored = new Set(b.erroredProviders ?? []);
  return (
    <span className={styles.inline}>
      {providers.map((p) => (
        <span key={p} className={styles.provider} data-error={errored.has(p) || undefined}
          title={errored.has(p) ? `${platformLabel(p)} — connection error` : platformLabel(p)}>
          <PlatformIcon platform={p} size={16} label={platformLabel(p)} />
        </span>
      ))}
    </span>
  );
}

const BAND_COLUMNS: DataTableColumn<AdminBandRow>[] = [
  {
    key: "band",
    header: "Band",
    cell: (b) => (
      <span className={styles.inline}>
        <strong>{b.name}</strong>
        {b.isFixture && <Fixture />}
        {b.status !== "active" && (
          <Tag tone="warning" size="sm">
            {b.status}
          </Tag>
        )}
      </span>
    ),
  },
  { key: "plan", header: "Plan", cell: (b) => <Plan b={b} /> },
  { key: "members", header: "Members", align: "end", cell: (b) => exact(b.members) },
  { key: "releases", header: "Releases", align: "end", cell: (b) => exact(b.releases) },
  { key: "orgs", header: "Orgs", align: "end", cell: (b) => exact(b.orgLinks) },
  { key: "active", header: "Last active", cell: (b) => ago(b.lastActiveOn, "Never") },
  { key: "connected", header: "Connected", cell: (b) => <Connections b={b} /> },
  { key: "created", header: "Created", cell: (b) => day(b.createdOn) },
];

export function BandsTab() {
  const q = useAdminBands();
  const [query, setQuery] = useState("");
  const [hideFixtures, setHideFixtures] = useState(false);

  const rows = useMemo(
    () => (q.data ?? []).filter((b) => (!hideFixtures || !b.isFixture) && matches(query, b.name, b.slug, b.ownerEmail)),
    [q.data, query, hideFixtures],
  );

  if (q.isLoading) return <Loading title="Bands" />;
  if (q.isError) return <Failed title="Bands" error={q.error} />;
  const all = q.data ?? [];

  return (
    <Card title="Bands" subtitle="Every band on the platform. Members excludes the artist account itself; last active is the latest sign-in activity of anyone with a seat.">
      <div className={styles.stack}>
        <ListControls query={query} onQuery={setQuery} hideFixtures={hideFixtures} onHideFixtures={setHideFixtures}
          placeholder="Band, slug or owner email" count={`${rows.length} of ${all.length}`} />
        {rows.length === 0 ? (
          <EmptyState title="No bands" description={all.length === 0 ? "Nobody has created a band yet." : "Nothing matches."} />
        ) : (
          <DataTable columns={BAND_COLUMNS} rows={rows} rowKey={(b) => b.bandId ?? ""} pageSize={50} label="Bands pages" />
        )}
      </div>
    </Card>
  );
}

// ---- Socials -----------------------------------------------------------------------------------------------

/** The platforms shown first, in this order; anything else that has landed follows alphabetically. */
const PLATFORM_ORDER = ["spotify", "instagram", "tiktok", "youtube", "facebook"];

interface SocialBand {
  bandId: string;
  name: string;
  isFixture: boolean;
  byPlatform: Map<string, AdminSocialRow>;
}

/** The latest change, exactly as the band's own home screen shows it (the server's HeroDeltas): nothing at all when
 * there is no qualifying pair, never a fabricated "+0%". */
function Change({ delta }: { delta: AdminSocialRow["followersChange"] }) {
  if (delta?.value == null) return null;
  return <StatDelta value={delta.value} unit={delta.unit === "absolute" ? "absolute" : "percent"} />;
}

function AudienceCell({ row }: { row: AdminSocialRow | undefined }) {
  if (!row) return <span className={styles.zero}>{dash}</span>;
  return (
    <span className={styles.audience} title={`Landed ${ago(row.asOf)}`}>
      <span className={styles.inline}>
        <span>{figure(row.followers)}</span>
        <Change delta={row.followersChange} />
      </span>
      {row.monthlyListeners != null && (
        <span className={`${styles.sub} ${styles.inline}`}>
          {figure(row.monthlyListeners)} monthly
          <Change delta={row.monthlyListenersChange} />
        </span>
      )}
    </span>
  );
}

export function SocialsTab() {
  const q = useAdminSocials();
  const [query, setQuery] = useState("");
  const [hideFixtures, setHideFixtures] = useState(false);

  const { bands, platforms } = useMemo(() => {
    const byBand = new Map<string, SocialBand>();
    const seen = new Set<string>();
    for (const r of q.data ?? []) {
      const id = r.bandId ?? "";
      const band = byBand.get(id) ?? { bandId: id, name: r.bandName ?? "", isFixture: !!r.isFixture, byPlatform: new Map() };
      if (r.platform) {
        band.byPlatform.set(r.platform, r);
        seen.add(r.platform);
      }
      byBand.set(id, band);
    }
    const ordered = [
      ...PLATFORM_ORDER.filter((p) => seen.has(p)),
      ...[...seen].filter((p) => !PLATFORM_ORDER.includes(p)).sort(),
    ];
    return { bands: [...byBand.values()], platforms: ordered };
  }, [q.data]);

  const rows = useMemo(
    () => bands.filter((b) => (!hideFixtures || !b.isFixture) && matches(query, b.name)),
    [bands, query, hideFixtures],
  );

  const columns: DataTableColumn<SocialBand>[] = useMemo(
    () => [
      {
        key: "band",
        header: "Band",
        cell: (b) => (
          <span className={styles.inline}>
            <strong>{b.name}</strong>
            {b.isFixture && <Fixture />}
          </span>
        ),
      },
      ...platforms.map<DataTableColumn<SocialBand>>((p) => ({
        key: p,
        header: (
          <span className={styles.inline}>
            <PlatformIcon platform={p} size={16} />
            {platformLabel(p)}
          </span>
        ),
        align: "end",
        cell: (b) => <AudienceCell row={b.byPlatform.get(p)} />,
      })),
    ],
    [platforms],
  );

  if (q.isLoading) return <Loading title="Socials" />;
  if (q.isError) return <Failed title="Socials" error={q.error} />;

  return (
    <Card title="Socials" subtitle="Followers on every platform each band has data for — the same count their own tiles show — with the latest day-over-day change their home screen would show. Spotify adds monthly listeners. Hover a figure for when it landed; a dash means nothing has landed for that platform.">
      <div className={styles.stack}>
        <ListControls query={query} onQuery={setQuery} hideFixtures={hideFixtures} onHideFixtures={setHideFixtures}
          placeholder="Band" count={`${rows.length} band${rows.length === 1 ? "" : "s"} with data`} />
        {rows.length === 0 ? (
          <EmptyState title="No audience data" description={bands.length === 0 ? "No band has synced a platform yet." : "Nothing matches."} />
        ) : (
          <DataTable columns={columns} rows={rows} rowKey={(b) => b.bandId} pageSize={50} label="Socials pages" />
        )}
      </div>
    </Card>
  );
}

// ---- People ------------------------------------------------------------------------------------------------

const PEOPLE_COLUMNS: DataTableColumn<AdminPersonRow>[] = [
  {
    key: "person",
    header: "Person",
    cell: (p) => (
      <span className={styles.person}>
        <span className={styles.inline}>
          <strong>{p.name}</strong>
          {p.isAdmin && (
            <Tag tone="accent" size="sm">
              admin
            </Tag>
          )}
          {p.isFixture && <Fixture />}
          {p.status !== "active" && (
            <Tag tone="warning" size="sm">
              {p.status}
            </Tag>
          )}
        </span>
        <span className={styles.sub}>{p.email}</span>
      </span>
    ),
  },
  { key: "bands", header: "Band seats", align: "end", cell: (p) => exact(p.bandSeats) },
  { key: "orgs", header: "Org seats", align: "end", cell: (p) => exact(p.orgSeats) },
  { key: "seen", header: "Last signed in", cell: (p) => ago(p.lastSeenOn, "Never") },
  { key: "verified", header: "Email", cell: (p) => (p.emailVerified ? "Verified" : <span className={styles.zero}>Unverified</span>) },
  { key: "push", header: "Push devices", align: "end", cell: (p) => exact(p.pushDevices) },
  { key: "created", header: "Joined", cell: (p) => day(p.createdOn) },
];

export function PeopleTab() {
  const q = useAdminPeople();
  const [query, setQuery] = useState("");
  const [hideFixtures, setHideFixtures] = useState(false);

  const rows = useMemo(
    () => (q.data ?? []).filter((p) => (!hideFixtures || !p.isFixture) && matches(query, p.name, p.email)),
    [q.data, query, hideFixtures],
  );

  if (q.isLoading) return <Loading title="People" />;
  if (q.isError) return <Failed title="People" error={q.error} />;
  const all = q.data ?? [];

  return (
    <Card title="People" subtitle="Every account. Last signed in ignores dev “View as” sessions.">
      <div className={styles.stack}>
        <ListControls query={query} onQuery={setQuery} hideFixtures={hideFixtures} onHideFixtures={setHideFixtures}
          placeholder="Name or email" count={`${rows.length} of ${all.length}`} />
        {rows.length === 0 ? (
          <EmptyState title="No people" description="Nothing matches." />
        ) : (
          <DataTable columns={PEOPLE_COLUMNS} rows={rows} rowKey={(p) => p.personId ?? ""} pageSize={50} label="People pages" />
        )}
      </div>
    </Card>
  );
}
